import { v4 as uuidv4 } from 'uuid';
import i18n from '../../../../../utils/i18n';

/**
 * Transform form data to database format for creating/updating lectures
 */
export const transformFormToDBFormat = (formData, companyId, eventId, industryId) => {
  // Helper function to convert string or array to MultiLangString format
  const convertToMultiLang = (value) => {
    if (!value) return [];
    
    // If it's already in the correct format
    if (Array.isArray(value)) {
      return value;
    }
    
    // If it's a string, create multilingual entries
    if (typeof value === 'string') {
      return [
        { language: 'de', text: value },
        { language: 'en', text: value }
      ];
    }
    
    return [];
  };

  // Helper function to convert description to content format
  const convertDescriptionToContent = (description) => {
    if (!description) return [];
    
    // If description is already in multilingual format
    if (Array.isArray(description)) {
      return description;
    }
    
    // If it's a string, use it for the current language
    if (typeof description === 'string') {
      const language = formData.language || i18n.language || 'en';
      return [
        { language, text: description }
      ];
    }
    
    return formData.content || [];
  };

  // Transform subjects from form format to DB format
  const transformSubjects = (subjects) => {
    if (!subjects || !Array.isArray(subjects)) return [];
    
    return subjects.map(subject => ({
      id: subject.id || uuidv4(),
      name: [
        { language: 'de', text: subject.de || '' },
        { language: 'en', text: subject.en || '' }
      ]
    }));
  };

  // Transform conditions - merge generalConditions and conditions
  const transformConditions = () => {
    const conditions = [];
    
    // Add general conditions
    if (formData.generalConditions && Array.isArray(formData.generalConditions)) {
      formData.generalConditions.forEach(condition => {
        conditions.push({
          name: condition.id,
          accepted: condition.accepted || false
        });
      });
    }
    
    // Add other conditions
    if (formData.conditions && Array.isArray(formData.conditions)) {
      formData.conditions.forEach(condition => {
        // Avoid duplicates
        if (!conditions.find(c => c.name === condition.name)) {
          conditions.push(condition);
        }
      });
    }
    
    return conditions;
  };

  // Get main speaker ID
  const getMainSpeakerId = () => {
    if (!formData.speakers || !Array.isArray(formData.speakers)) return null;
    
    const mainSpeaker = formData.speakers.find(s => s.type === 'main_speaker');
    return mainSpeaker ? (mainSpeaker.employeeId || mainSpeaker.id) : null;
  };

  // Get all speaker IDs
  const getSpeakerIds = () => {
    if (!formData.speakers || !Array.isArray(formData.speakers)) return [];
    
    return formData.speakers.map(speaker => speaker.employeeId || speaker.id);
  };

  // Determine which PDF field to use based on the new handout structure
  const getPdfFields = () => {
    const pdfFields = {};
    
    if (formData.handout) {
      // New structure: handout is an object with 'de' and/or 'en' keys
      if (formData.handout.de) {
        pdfFields.pdfDe = {
          fileName: formData.handout.de.fileName,
          name: formData.handout.de.name || formData.handout.de.fileName,
          s3Path: formData.handout.de.s3Path || formData.handout.de.fileName
        };
      }
      
      if (formData.handout.en) {
        pdfFields.pdfEn = {
          fileName: formData.handout.en.fileName,
          name: formData.handout.en.name || formData.handout.en.fileName,
          s3Path: formData.handout.en.s3Path || formData.handout.en.fileName
        };
      }
    }
    
    return pdfFields;
  };

  // Build the transformed data object
  const transformedData = {
    // Direct mappings
    type: formData.type || 'unset_lecture',
    presentationLanguage: formData.language ? formData.language.toUpperCase() : undefined,
    
    // Company, Event and Industry IDs
    companyId: companyId,
    eventId: eventId,
    industryLecturesId: industryId,
    
    // Transform multilingual fields
    title: formData.title ? convertToMultiLang(formData.title) : [],
    content: formData.content || convertDescriptionToContent(formData.description),
    
    // Transform subjects
    subjects: transformSubjects(formData.subjects),
    
    // Transform conditions
    conditions: transformConditions(),
    
    // File references
    image: formData.image ? {
      fileName: formData.image.fileName,
      alt: formData.image.alt || 'Lecture Image'
    } : null,
    
    // PDFs based on language
    ...getPdfFields(),
    
    // Presentation file
    presentation: formData.presentation ? {
      fileName: formData.presentation.fileName,
      name: formData.presentation.name,
      s3Path: formData.presentation.s3Path
    } : null,
    
    // Speaker IDs (for relations)
    speakers: getSpeakerIds(),
    topics: formData.topics || [],
    mainSpeakerId: getMainSpeakerId()
  };

  return transformedData;
};

/**
 * Transform database data to form format for editing
 */
export const transformDBToFormFormat = (lectureData, employees = []) => {
  if (!lectureData) return null;

  // Helper function to extract text from MultiLangString
  const extractMultiLangText = (multiLangArray, asString = false) => {
    if (!multiLangArray || !Array.isArray(multiLangArray)) return asString ? undefined : [];
    
    if (asString) {
      // Return the text for the presentation language if available
      const lang = lectureData.presentationLanguage ? lectureData.presentationLanguage.toLowerCase() : null;
      if (lang) {
        const item = multiLangArray.find(m => m.language === lang);
        if (item?.text) return item.text;
      }
      // Return first available text if no language match
      return multiLangArray[0]?.text || undefined;
    }
    
    return multiLangArray;
  };

  // Transform speakers from IDs to full objects
  const transformSpeakers = () => {
    const speakers = [];
    
    // Add speakers from the many-to-many relation
    if (lectureData.speakers?.items) {
      lectureData.speakers.items.forEach((speakerRelation, index) => {
        const employee = speakerRelation.employee || 
                        employees.find(emp => emp.id === speakerRelation.employeeId);
        
        if (employee) {
          const isMainSpeaker = employee.id === lectureData.lectureMainSpeakerId ||
                               employee.id === lectureData.mainSpeaker?.id;
          
          speakers.push({
            id: employee.id,
            employeeId: employee.id,
            firstName: employee.user?.firstName || '',
            lastName: employee.user?.lastName || '',
            middleName: employee.user?.middleName || '',
            title: employee.user?.title || '',
            image: employee.image ? {
              ...employee.image,
              alt: employee.image.alt || ''  // Ensure alt is always a string
            } : null,
            type: isMainSpeaker ? 'main_speaker' : 'co_speaker'
          });
        }
      });
    }
    
    // Ensure main speaker is included if not already
    if (lectureData.mainSpeaker && !speakers.find(s => s.id === lectureData.mainSpeaker.id)) {
      const mainSpeaker = lectureData.mainSpeaker;
      speakers.unshift({
        id: mainSpeaker.id,
        employeeId: mainSpeaker.id,
        firstName: mainSpeaker.user?.firstName || mainSpeaker.firstName || '',
        lastName: mainSpeaker.user?.lastName || mainSpeaker.lastName || '',
        middleName: mainSpeaker.user?.middleName || mainSpeaker.middleName || '',
        title: mainSpeaker.user?.title || mainSpeaker.title || '',
        image: mainSpeaker.image ? {
          ...mainSpeaker.image,
          alt: mainSpeaker.image.alt || ''  // Ensure alt is always a string
        } : null,
        type: 'main_speaker'
      });
    }
    
    return speakers;
  };

  // Transform subjects from DB format to form format
  const transformSubjects = () => {
    if (!lectureData.subjects || !Array.isArray(lectureData.subjects)) return [];
    
    return lectureData.subjects.map(subject => ({
      id: subject.id,
      de: subject.name?.find(n => n.language === 'de')?.text || '',
      en: subject.name?.find(n => n.language === 'en')?.text || ''
    }));
  };

  // Transform topics from relation items to array of IDs
  const transformTopics = () => {
    if (!lectureData.topics?.items) return [];
    return lectureData.topics.items.map(item => item.topicId);
  };

  // Split conditions into generalConditions and conditions
  const transformConditions = () => {
    const generalConditionIds = [
      'submission_deadlines',
      'advertising_free_content', 
      'recording_policy',
      'program_scheduling',
      'file_upload_deadlines',
      'speaker_expertise'
    ];
    
    const generalConditions = [];
    const otherConditions = [];
    
    if (lectureData.conditions && Array.isArray(lectureData.conditions)) {
      lectureData.conditions.forEach(condition => {
        if (generalConditionIds.includes(condition.name)) {
          generalConditions.push({
            id: condition.name,
            accepted: condition.accepted
          });
        } else {
          otherConditions.push(condition);
        }
      });
    }
    
    return { generalConditions, conditions: otherConditions };
  };

  // Transform handout from pdfDe and pdfEn to new structure
  const getHandout = () => {
    const handout = {};
    
    // Add German handout if exists
    if (lectureData.pdfDe) {
      handout.de = {
        fileName: lectureData.pdfDe.fileName,
        name: lectureData.pdfDe.name,
        s3Path: lectureData.pdfDe.s3Path
      };
    }
    
    // Add English handout if exists
    if (lectureData.pdfEn) {
      handout.en = {
        fileName: lectureData.pdfEn.fileName,
        name: lectureData.pdfEn.name,
        s3Path: lectureData.pdfEn.s3Path
      };
    }
    
    // Return null if no handouts exist, otherwise return the object
    return (handout.de || handout.en) ? handout : null;
  };

  const { generalConditions, conditions } = transformConditions();

  // Build the transformed form data
  const formData = {
    // Basic fields
    type: lectureData.type,
    language: lectureData.presentationLanguage ? lectureData.presentationLanguage.toLowerCase() : undefined,
    
    // Transform multilingual fields
    title: extractMultiLangText(lectureData.title, false), // Keep as array, not string
    description: extractMultiLangText(lectureData.content, true),
    content: lectureData.content,
    
    // Transform complex fields
    speakers: transformSpeakers(),
    subjects: transformSubjects(),
    topics: transformTopics(),
    generalConditions,
    conditions,
    
    // File fields
    image: lectureData.image,
    handout: getHandout(),
    presentation: lectureData.presentation,
    
    // Meta fields for tracking
    hasReadInformation: true,
    isValid: false,
    completedSteps: [],
    currentStep: 'information'
  };

  return formData;
};

/**
 * Validate if the form data has all required fields for submission
 */
export const validateFormDataForSubmission = (formData) => {
  const errors = [];
  
  // Required fields check
  if (!formData.type) errors.push('Lecture type is required');
  if (!formData.language) errors.push('Presentation language is required');
  if (!formData.title || formData.title.length === 0) errors.push('Title is required');
  if (!formData.speakers || formData.speakers.length === 0) errors.push('At least one speaker is required');
  if (!formData.topics || formData.topics.length === 0) errors.push('At least one topic is required');
  
  // Check for main speaker
  const hasMainSpeaker = formData.speakers?.some(s => s.type === 'main_speaker');
  if (!hasMainSpeaker) errors.push('A main speaker must be designated');
  
  return {
    isValid: errors.length === 0,
    errors
  };
};